import {
  makeStyles,
  mergeClasses,
  tokens,
  shorthands,
  Button,
  Tab,
  TabList,
  Text,
  Spinner,
  Link,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Badge,
} from '@fluentui/react-components';
import {
  OpenRegular,
  BotFilled,
  MoreHorizontalRegular,
  BugRegular,
} from '@fluentui/react-icons';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { isAgentAction, isCodingAgentAction, isFoundryAgentAction, isFoundryAgentModelType } from '@/types/workflowDefinition';
import type { Action } from '@/types/workflowDefinition';
import {
  AgentVariantIcon,
  useAgentBadgeClass,
} from '../../helpers/agentVariant';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { readAgentModelType } from '../../workflowHelpers';
import { ConnectionErrorBanner } from '../compact/ConnectionErrorBanner';
import { ParameterList } from '../compact/ParameterList';
import { HostedAgentConfigPanel } from '../hostedAgent/HostedAgentConfigPanel';
import { HostedAgentConnectionTab } from '../hostedAgent/HostedAgentConnectionTab';
import { FoundryDetailsTab } from '../foundry/FoundryDetailsTab';
import { SettingsPanel } from '../settings/SettingsPanel';
import { ConnectionTab } from '../connections/ConnectionTab';
import { ToolConfigTab } from '../tool/ToolConfigTab';
import { AgentHarnessSelector } from '../sandbox/AgentHarnessSelector';
import { WorkflowInputPanel } from './WorkflowInputPanel';
import { RunDataPanel } from '../run-data/RunDataPanel';
import { useNodeSettings } from '@/hooks/useNodeSettings';
import { useTokenSources, isConnectedToTrigger } from '@/hooks/useTokenSources';
import { useAppLevelParameters } from '@/hooks/useAppLevelParameters';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useUpstreamManifests } from '@/hooks/useUpstreamManifests';
import { usePreviewOutputs } from '@/hooks/useNodePreviewData';
import { operationRequiresConnection } from '@/types/connectionValidation';
import { getBuiltInManifest } from '@/data/manifests';
import { requiresConnection } from '@/types/operationManifest';
import type { OperationInfo } from '@/types/operationManifest';
import { buildSerializedNodeData } from '@/stores/nodeEditorStore';
import { cleanAction } from '@/services/workflowSerializer';
import type { NodeSettings } from '@/types/nodeSettings';
import type { Token } from '@/types/valueSegment';
import type { AgentTool } from '@/types/workflowDefinition';
import { useConnectionStore } from '@/stores/connectionStore';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { isFeatureEnabled } from '@/featureFlags';
import { KnowledgeBaseSelector } from '../KnowledgeBaseSelector';
import { MockDataTab } from '../mock/MockDataTab';
import { McpToolConfigTab } from '../tool/McpToolConfigTab';
import { isMcpAgentTool } from '@/types/workflowDefinition';
import { NodeCodeView } from './NodeCodeView';
import { useTabOverflow } from './useTabOverflow';

/**
 * Determine if this node type should have a Connection tab.
 * This includes both standard single-slot and Agent multi-slot connections.
 * Checks the manifest's connection.required property first, then falls back to type-based check.
 */
function needsConnectionTab(nodeType: string | undefined): boolean {
  if (!nodeType) return false;

  // Check manifest first - it's the source of truth for connection requirements
  const manifest = getBuiltInManifest(nodeType);
  if (manifest?.properties.connection !== undefined) {
    return requiresConnection(manifest);
  }

  // Fall back to type-based check for operations without manifests
  return operationRequiresConnection(nodeType);
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },

  // Main content area with three panels
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },

  // Left panel - Input (resizable)
  leftPanel: {
    flexShrink: 0,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    position: 'relative',
  },

  // Center panel - Editor
  centerPanel: {
    flex: 1,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: '400px',
  },
  // Modifier for the configure-tab usage where the editor must be allowed to
  // shrink below 400px (e.g. when the right/left side panels claim more space).
  centerPanelShrinkable: {
    minWidth: 0,
  },

  // Right panel - Output (resizable)
  rightPanel: {
    flexShrink: 0,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    position: 'relative',
  },

  // Resizer handle
  resizer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '6px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    zIndex: 10,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  resizerLeft: {
    right: '-3px',
  },
  resizerRight: {
    left: '-3px',
  },
  resizerActive: {
    backgroundColor: tokens.colorBrandBackground,
  },

  // Panel header
  panelHeader: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  panelTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Panel content
  panelContent: {
    flex: 1,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    overflow: 'auto',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...customScrollbarStyles,
  },

  // Tab list container with overflow handling
  tabListContainer: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalXS,
    flexShrink: 0,
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  // Tab list
  tabList: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  // Overflow menu button
  overflowButton: {
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalXS,
  },

  // Editor content (scrollable)
  editorContent: {
    flex: 1,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    overflow: 'auto',
    padding: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    ...customScrollbarStyles,
  },

  /**
   * Read-only mode for the form area.
   *
   * Strategy:
   *   - The Settings tab passes `readOnly={isReadOnly}` to SettingsPanel
   *     so Fluent's own native disabled state renders cleanly for
   *     Switch / SpinButton / Field (no grey-blob bleed).
   *   - The Parameters tab relies on store-level guards in
   *     nodeEditorStore (every mutating action early-returns when
   *     isReadOnly is true) plus TokenizedInput's own store gate.
   *   - We add a global `opacity` fade for visual feedback and block
   *     pointer events on text-like form controls so users don't waste
   *     clicks. We deliberately exclude Switch/Checkbox/Radio inputs
   *     here because Fluent's Switch renders a hidden
   *     `<input type="checkbox">` that would pick up the grey if we
   *     used a broad `& input` selector.
   */
  editorContentReadOnly: {
    opacity: 0.9,
    // Disable interaction on text-like editable controls without
    // recoloring them (Fluent already styles disabled states via the
    // readOnly prop / store guards).
    '& input[type="text"], & input[type="number"], & input[type="email"], & input[type="search"], & input[type="url"], & input[type="tel"], & input[type="password"], & input:not([type]), & textarea, & [contenteditable="true"], & [role="combobox"], & [role="listbox"], & [role="spinbutton"], & [role="slider"]':
      {
        pointerEvents: 'none',
        cursor: 'not-allowed',
      },
  },

  // Data tree
  dataTree: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    overflow: 'auto',
    flex: 1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },

  // Status section
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalM,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  statusLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  statusValue: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  statusSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusRunning: {
    color: tokens.colorPaletteBlueForeground2,
  },

  // Run history
  runHistory: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  runHistoryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  runHistoryNumber: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    width: '32px',
  },
  runHistoryStatus: {
    fontSize: tokens.fontSizeBase200,
  },
  runHistoryDuration: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
  },



  // Panel actions
  panelActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
  },

  // Settings tab styles
  settingsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  settingsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  settingsGroupTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXS,
  },
  settingsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  settingsLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  settingsDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // About tab styles
  aboutSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  aboutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  aboutIcon: {
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeHero700,
  },
  aboutIconDefault: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground2,
  },
  aboutInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
  },
  aboutName: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  aboutType: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  aboutDetailRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
  },
  aboutDetailLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minWidth: '100px',
  },
  aboutDetailValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  aboutDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
  },
  // Debug mode indicator
  debugModeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorPalettePurpleBackground2,
  },
  debugModeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorPalettePurpleForeground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
  },

  panelContentCentered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Compact (narrow window) layout — 3 outer tabs replacing the 3 side-by-side panels
  compactLayout: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  compactTabBar: {
    flexShrink: 0,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingLeft: tokens.spacingHorizontalM,
  },
  compactTabContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  compactPanelContent: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...customScrollbarStyles,
  },

  // Hidden strip used to measure actual tab widths so the overflow calculation
  // matches the real rendered size of each label.
  tabMeasurement: {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    height: 0,
    overflow: 'hidden',
  },
});

type EditorTab = 'parameters' | 'connection' | 'settings' | 'agent-harness' | 'knowledge' | 'tool' | 'mcp-config' | 'mock' | 'code' | 'about';

// Tab configuration with labels
const TAB_CONFIG: { id: EditorTab; label: string }[] = [
  { id: 'parameters', label: 'Parameters' },
  { id: 'connection', label: 'Connection' },
  { id: 'tool', label: 'Tool' },
  { id: 'mcp-config', label: 'MCP server' },
  { id: 'settings', label: 'Settings' },
  { id: 'agent-harness', label: 'Agent Harness' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'mock', label: 'Test data' },
  { id: 'code', label: 'Code' },
  { id: 'about', label: 'About' },
];

// Approximate width per tab (including padding)
const OVERFLOW_BUTTON_WIDTH = 40;

/**
 * ExpandedView - The fullscreen three-panel layout for node editing
 */
// Panel size constraints
const COMPACT_BREAKPOINT = 900; // Switch to tabbed layout when narrower than this
const LEFT_PANEL_MIN = 200;
const LEFT_PANEL_DEFAULT = 320;
const LEFT_PANEL_MAX = 600;
const RIGHT_PANEL_MIN = 200;
const RIGHT_PANEL_DEFAULT = 360;
const RIGHT_PANEL_MAX = 600;

export const ExpandedView = () => {
  const styles = useStyles();
  const badgeClass = useAgentBadgeClass();
  const initialTab = useNodeEditorStore((state) => state.initialTab);
  const clearInitialTab = useNodeEditorStore((state) => state.clearInitialTab);
  const inputPanelValueSource = useNodeEditorStore((state) => state.inputPanelValueSource);
  const isReadOnly = useNodeEditorStore((state) => state.isReadOnly);
  const setInputPanelValueSource = useNodeEditorStore(
    (state) => state.setInputPanelValueSource,
  );
  const [activeTab, setActiveTab] = useState<EditorTab>(initialTab ?? 'parameters');

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(LEFT_PANEL_DEFAULT);
  const [rightPanelWidth, setRightPanelWidth] = useState(RIGHT_PANEL_DEFAULT);
  const [resizingPanel, setResizingPanel] = useState<'left' | 'right' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Compact layout state
  const [containerWidth, setContainerWidth] = useState(0);
  const [outerTab, setOuterTab] = useState<'input' | 'configure' | 'output'>('configure');

  // Handle resize start
  const handleResizeStart = useCallback((panel: 'left' | 'right', e: React.MouseEvent) => {
    e.preventDefault();
    setResizingPanel(panel);
    startXRef.current = e.clientX;
    startWidthRef.current = panel === 'left' ? leftPanelWidth : rightPanelWidth;
  }, [leftPanelWidth, rightPanelWidth]);

  // Handle resize move and end
  useEffect(() => {
    if (!resizingPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;

      if (resizingPanel === 'left') {
        // Left panel: dragging right increases width
        const newWidth = Math.min(LEFT_PANEL_MAX, Math.max(LEFT_PANEL_MIN, startWidthRef.current + delta));
        setLeftPanelWidth(newWidth);
      } else {
        // Right panel: dragging left increases width
        const newWidth = Math.min(RIGHT_PANEL_MAX, Math.max(RIGHT_PANEL_MIN, startWidthRef.current - delta));
        setRightPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setResizingPanel(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingPanel]);

  // Clear the initial tab after using it (so subsequent opens don't retain it)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
      clearInitialTab();
    }
  }, [initialTab, clearInitialTab]);

  // Track container width to decide between 3-panel and compact tabbed layout
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isCompact = containerWidth > 0 && containerWidth < COMPACT_BREAKPOINT;

  const {
    node,
    parameterValues,
    settingsValues,
    settingsValidationErrors,
    setSettings,
    initializeSettings,
  } = useNodeEditorStore();

  // Get execution mode state for debug mode
  const isExecutionMode = useEditorRunStore((state) => state.isExecutionMode);

  // Get agent connection endpoint for Foundry agents (used by the about tab)
  const agentConnectionsConfig = useConnectionStore((state) => state.originalConnectionsConfig?.agentConnections);

  // Derive operation info from parameter values
  const operationInfo = useMemo<OperationInfo | null>(() => {
    const apiId = parameterValues['_apiId'] as string | undefined;
    const operationId = parameterValues['_operationId'] as string | undefined;

    if (!apiId || !operationId) return null;

    return {
      connectorId: apiId,
      operationId,
    };
  }, [parameterValues]);

  // Get settings from manifest (or defaults based on node type)
  // Pass nodeData to extract existing settings from workflow definition
  const nodeId = node?.id ?? null;
  const mockedNode = useWorkflowStore((state) => (nodeId ? state.mockedNodes[nodeId] : undefined));
  const nodeType = node?.nodeData?.type;
  // The Mock Data tab shows whenever this node has a mock entry (recipe-seeded).
  const isMockedNode = !!mockedNode;
  const {
    settings: manifestSettings,
    isTrigger,
    isLoading: settingsLoading,
    error: settingsError,
    availablePredecessors,
  } = useNodeSettings(nodeId, operationInfo, nodeType, node?.nodeData);

  // Initialize settings from manifest when available (only if not already initialized)
  useEffect(() => {
    if (manifestSettings && !settingsValues) {
      initializeSettings(manifestSettings);
    }
  }, [manifestSettings, settingsValues, initializeSettings]);

  // Handle settings changes
  const handleSettingsChange = useCallback((newSettings: NodeSettings) => {
    setSettings(newSettings);
  }, [setSettings]);

  // Get the agent parameter schema — prefer draft from parameterValues, fall back to node data.
  const agentParameterSchema = useMemo(() => {
    const draft = parameterValues['_agentParameterSchema'] as Record<string, unknown> | null | undefined;
    if (draft !== undefined) return draft ?? undefined;
    if (node?.toolData?.agentParameterSchema) {
      return node.toolData.agentParameterSchema as Record<string, unknown>;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameterValues['_agentParameterSchema'], node?.toolData?.agentParameterSchema]);

  // Fetch manifests and swaggers for upstream nodes
  const {
    manifests: upstreamManifests,
    swaggers: upstreamSwaggers,
    isLoading: upstreamLoading,
  } = useUpstreamManifests(node?.id);

  // Get workflow actions to check if node is connected to trigger
  const actions = useWorkflowStore((state) => state.workflow?.definition?.actions ?? {});

  // Only include trigger tokens if the node is connected to the trigger
  const includeTrigger = useMemo(() => {
    return isConnectedToTrigger(node?.id, actions, node?.parentAgentName);
  }, [node?.id, actions, node?.parentAgentName]);

  // App-level parameters from `parameters.json` so the inline Input rail
  // surfaces `@parameters('name')` tokens alongside workflow-definition ones.
  const appLevelParameters = useAppLevelParameters();

  // Get token sources for the Input panel
  const { sources: tokenSources, isLoading: tokensLoading } = useTokenSources({
    currentNodeId: node?.id,
    parentAgentName: node?.parentAgentName,
    scopeContext: node?.scopeContext,
    includeTrigger,
    includeParameters: true,
    includeVariables: true,
    includeAgentParameters: !!node?.parentAgentName,
    manifests: upstreamManifests,
    swaggers: upstreamSwaggers,
    agentParameterSchema,
    ...(appLevelParameters ? { appLevelParameters } : {}),
  });

  // Get preview output data from last successful run for token previews
  const {
    outputs: previewOutputs,
    variables: variableOutputs,
    hasRunData: hasPreviewData,
    isLoading: isLoadingPreviewData,
  } = usePreviewOutputs(node?.id ?? null);

  // Combine action outputs with variable values for expression evaluation
  const combinedPreviewOutputs = useMemo(() => {
    return {
      ...previewOutputs,
      // Add a special key for variables so TokenizedInput can access them
      __variables__: variableOutputs,
    };
  }, [previewOutputs, variableOutputs]);

  // Open token picker targeting a new field with this token pre-selected.
  // For now, copy to clipboard for easy paste (silent on failure).
  const handleTokenClick = useCallback((token: Token) => {
    if (token.value) {
      navigator.clipboard.writeText(token.value).catch(() => {
        // Clipboard API may be unavailable (insecure context, missing perms).
      });
    }
  }, []);

  // Function-template insert from the rail's Expressions tab. Lands the
  // template in the parameter that most recently held focus; falls back to
  // clipboard when no field is focused (e.g. rail opened by keyboard before
  // any field was active).
  const handleExpressionInsert = useCallback((template: string) => {
    if (!template) return;
    const lastFocused =
      useNodeEditorStore.getState().lastFocusedParameterField;
    if (lastFocused) {
      useNodeEditorStore.getState().insertExpressionToParameter(lastFocused, template);
      return;
    }
    console.debug(
      '[ExpandedView] No focused parameter field for expression insert — falling back to clipboard',
    );
    navigator.clipboard.writeText(template).catch(() => {
      // Clipboard API may be unavailable (insecure context, missing perms).
      // Silent failure matches handleTokenClick — the function picker has
      // its own visible Insert button, so users can retry from there.
    });
  }, []);

  // Derive node-dependent values (must be before early return for hooks below)
  const isAgent = node?.type === 'action' && node?.nodeData && isAgentAction(node.nodeData as Action);
  const isHostedAgent =
    isAgent &&
    (parameterValues['agentModelType'] === 'FoundryHostedAgent' ||
      parameterValues['parameters.agentModelType'] === 'FoundryHostedAgent');
  const isFoundryAgent = (node?.type === 'action' && node?.nodeData && isFoundryAgentAction(node.nodeData as Action))
    || (!isHostedAgent && isAgent && isFoundryAgentModelType(readAgentModelType(parameterValues)));
  // Coding Agent — base detection uses the action's serialized harness type,
  // but also honor the live AgentHarnessSelector value so a freshly seeded
  // node (where the tab is the source of truth) keeps the tab visible.
  const isCodingAgent =
    (node?.type === 'action' && node?.nodeData && isCodingAgentAction(node.nodeData as Action)) ||
    (isAgent && typeof parameterValues['agentModelSettings.agentHarness.type'] === 'string');
  const isTool = node?.type === 'tool';
  const isMcpTool = isTool && isMcpAgentTool(node?.toolData);
  const showConnectionTab = needsConnectionTab(nodeType) || isHostedAgent;

  // Build the list of available tabs based on node type
  const availableTabs = useMemo(() => {
    // MCP tools have no parameters / settings / connection / inner-action;
    // surface only the MCP server config and the About details tab.
    if (isMcpTool) {
      return TAB_CONFIG.filter(tab => tab.id === 'mcp-config' || tab.id === 'about');
    }
    return TAB_CONFIG.filter(tab => {
      if (tab.id === 'connection') return showConnectionTab;
      if (tab.id === 'mock') return isMockedNode;
      if (tab.id === 'knowledge') return isAgent && !isHostedAgent && isFeatureEnabled('knowledgeManagement');
      if (tab.id === 'tool') return isTool;
      if (tab.id === 'mcp-config') return false; // non-MCP tools never show this
      if (tab.id === 'agent-harness') return isCodingAgent && !isHostedAgent;
      return true;
    });
  }, [showConnectionTab, isMockedNode, isAgent, isHostedAgent, isCodingAgent, isTool, isMcpTool]);

  useEffect(() => {
    if (availableTabs.length === 0) {
      return;
    }

    const hasActiveTab = availableTabs.some(tab => tab.id === activeTab);
    if (!hasActiveTab && availableTabs[0]) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab, setActiveTab]);

  // Tab overflow: measures actual label widths via a hidden strip rendered
  // inside renderTabStrip(). The same strip is mounted in either the
  // compact or three-panel layout (never both), so the hook's ref
  // callbacks rebind cleanly on layout switches.
  const {
    setContainerRef: setTabContainerRef,
    setMeasurementRef: setTabMeasurementRef,
    visibleTabs,
    overflowTabs,
  } = useTabOverflow(availableTabs, activeTab, OVERFLOW_BUTTON_WIDTH);
  const hasOverflow = overflowTabs.length > 0;

  // Get swagger metadata for proper input serialization
  const parameterDefinitions = useNodeEditorStore((state) => state.parameterDefinitions);
  const swaggerOperationMetadata = useNodeEditorStore((state) => state.swaggerOperationMetadata);
  // Picker display map so the live preview matches what `applyChanges` writes.
  const pickerDisplayValues = useNodeEditorStore((state) => state.pickerDisplayValues);

  // Build live preview of node data reflecting current parameter and settings changes
  // This shows users what will be saved without them having to close/reopen the node
  const workflowKind = useWorkflowStore((state) => state.workflow?.kind);

  const liveNodeData = useMemo(() => {
    if (!node?.nodeData) return null;

    try {
      const built = buildSerializedNodeData({
        nodeData: node.nodeData as Record<string, unknown>,
        nodeType: node.type,
        operationType: nodeType,
        parameterValues,
        settingsValues,
        parameterDefinitions: parameterDefinitions ?? undefined,
        swaggerOperationMetadata: swaggerOperationMetadata ?? undefined,
        pickerDisplayValues,
        workflowKind,
      });
      // Route through the same boundary the workflow serializer uses so the
      // node Code tab never shows client-only metadata (`swaggerPathTemplate`,
      // `position`, `detached`, …) that won't end up in workflow.json. Use
      // the recursive variant so nested scope branches (If/Switch/Foreach)
      // and Agent tool inner actions are cleaned the same way the save
      // pipeline cleans them.
      return cleanAction(built as Action, false) as Record<string, unknown>;
    } catch {
      // If serialization fails, fall back to original node data
      return node.nodeData as Record<string, unknown>;
    }
  }, [node, parameterValues, settingsValues, nodeType, parameterDefinitions, swaggerOperationMetadata, pickerDisplayValues, workflowKind]);

  // Early return after all hooks
  if (!node) return null;

  // For Agent nodes, connection is stored in modelConfigurations.model1, not in host.connection
  const connectionName = isAgent
    ? (parameterValues['_modelConnection_model1'] as string | undefined)
    : (parameterValues['_connection'] as string | undefined);

  // Get tool-specific data when editing a tool
  // toolData is now stored separately in node.toolData (not derived from nodeData)
  const toolData: AgentTool | undefined = isTool && node.toolData
    ? node.toolData
    : undefined;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'parameters':
        if (isHostedAgent) {
          return (
            <HostedAgentConfigPanel agentNodeName={node.name} />
          );
        }
        return <ParameterList showAgentParameterButton={!!node?.parentAgentName} />;

      case 'connection':
        if (isHostedAgent) {
          return <HostedAgentConnectionTab />;
        }
        return (
          <ConnectionTab
            nodeId={node.id}
            nodeType={nodeType}
            connectionName={connectionName}
          />
        );

      case 'knowledge':
        return <KnowledgeBaseSelector />;

      case 'settings':
        return (
          <SettingsPanel
            settings={settingsValues ?? manifestSettings ?? {} as NodeSettings}
            onChange={handleSettingsChange}
            isTrigger={isTrigger}
            availablePredecessors={availablePredecessors}
            isLoading={settingsLoading}
            error={settingsError}
            validationErrors={settingsValidationErrors}
            readOnly={isReadOnly}
          />
        );

      case 'agent-harness':
        return <AgentHarnessSelector />;

      case 'tool':
        return (
          <ToolConfigTab
            toolName={node.toolName ?? node.name}
            {...(toolData?.description && { toolDescription: toolData.description })}
            {...(toolData?.agentParameterSchema && { agentParameterSchema: toolData.agentParameterSchema as Record<string, unknown> })}
            {...(node.parentAgentName && { parentAgentName: node.parentAgentName })}
            {...(parameterValues['_workflowId'] && { targetWorkflowId: parameterValues['_workflowId'] as string })}
          />
        );

      case 'mcp-config':
        if (!toolData || !node.parentAgentName) return null;
        return (
          <McpToolConfigTab
            agentName={node.parentAgentName}
            toolName={node.toolName ?? node.name}
            toolData={toolData}
          />
        );

      case 'code':
        return (
          <NodeCodeView
            value={liveNodeData ?? node.nodeData}
            ariaLabel={`${node.name} JSON`}
          />
        );

      case 'mock':
        return nodeId ? <MockDataTab nodeId={nodeId} /> : null;
        
      case 'about':
        // Foundry agents get the specialized details tab with Tools/Knowledge/Memory/Guardrails
        if (isFoundryAgent) {
          return (
            <FoundryDetailsTab
              rawEndpoint={connectionName ? agentConnectionsConfig?.[connectionName]?.endpoint : undefined}
              connectionResourceId={connectionName ? agentConnectionsConfig?.[connectionName]?.resourceId : undefined}
              agentId={parameterValues['foundryAgentId'] as string | undefined}
              agentVersion={(parameterValues['foundryAgentVersion'] as 'v1' | 'v2' | undefined) ?? 'v2'}
            />
          );
        }
        return (
          <div className={styles.aboutSection}>
            {/* Node Header */}
            <div className={styles.aboutHeader}>
              <div
                className={mergeClasses(
                  styles.aboutIcon,
                  isCodingAgent
                    ? badgeClass('coding')
                    : isAgent
                      ? badgeClass('native')
                      : styles.aboutIconDefault,
                )}
              >
                {isCodingAgent ? (
                  <AgentVariantIcon variant="coding" />
                ) : (
                  <BotFilled />
                )}
              </div>
              <div className={styles.aboutInfo}>
                <Text className={styles.aboutName}>{node.name}</Text>
                <Text className={styles.aboutType}>
                  {isAgent ? 'AI Agent' : nodeType}
                  {node.type === 'trigger' && ' (Trigger)'}
                </Text>
              </div>
            </div>

            {/* Node Details */}
            <div className={styles.settingsGroup}>
              <Text className={styles.settingsGroupTitle}>Details</Text>
              <div className={styles.aboutDetailRow}>
                <Text className={styles.aboutDetailLabel}>Type:</Text>
                <Text className={styles.aboutDetailValue}>{nodeType}</Text>
              </div>
              <div className={styles.aboutDetailRow}>
                <Text className={styles.aboutDetailLabel}>Category:</Text>
                <Text className={styles.aboutDetailValue}>
                  {isAgent ? 'AI Agent' : node.type === 'trigger' ? 'Trigger' : 'Action'}
                </Text>
              </div>
              {node.parentAgentName && (
                <div className={styles.aboutDetailRow}>
                  <Text className={styles.aboutDetailLabel}>Parent Agent:</Text>
                  <Text className={styles.aboutDetailValue}>{node.parentAgentName}</Text>
                </div>
              )}
            </div>

            {/* Description */}
            <div className={styles.settingsGroup}>
              <Text className={styles.settingsGroupTitle}>Description</Text>
              <Text className={styles.aboutDescription}>
                {isAgent
                  ? 'An AI-powered agent that can process natural language instructions and execute complex tasks using available tools.'
                  : `This ${node.type} performs the ${nodeType} operation in your workflow.`}
              </Text>
            </div>

            {/* Documentation Link */}
            <div className={styles.settingsGroup}>
              <Text className={styles.settingsGroupTitle}>Resources</Text>
              <Link href="https://learn.microsoft.com/azure/logic-apps/" target="_blank">
                <Button appearance="subtle" icon={<OpenRegular />}>
                  View Documentation
                </Button>
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Editor tab strip with overflow handling. The hidden measurement strip
  // renders every available tab at natural width so useTabOverflow can
  // decide which fit. Only one instance of this is mounted at a time
  // (compact vs three-panel layout).
  const renderTabStrip = () => (
    <div className={styles.tabListContainer} ref={setTabContainerRef}>
      <TabList
        className={styles.tabList}
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as EditorTab)}
      >
        {visibleTabs.map(tab => (
          <Tab key={tab.id} value={tab.id}>{tab.label}</Tab>
        ))}
      </TabList>
      {hasOverflow && (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content="More tabs" relationship="label">
              <Button
                className={styles.overflowButton}
                appearance="subtle"
                icon={<MoreHorizontalRegular />}
                size="small"
                aria-label="More tabs"
              />
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {overflowTabs.map(tab => (
                <MenuItem key={tab.id} onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
      <div className={styles.tabMeasurement} ref={setTabMeasurementRef} aria-hidden="true">
        <TabList selectedValue={activeTab}>
          {availableTabs.map(tab => (
            <Tab key={`measure-${tab.id}`} value={`measure-${tab.id}`} tabIndex={-1}>{tab.label}</Tab>
          ))}
        </TabList>
      </div>
    </div>
  );

  return (
    <div className={styles.container} ref={containerRef}>
      {isCompact ? (
        /* Compact mode: 3 outer tabs replacing the 3 side-by-side panels */
        <div className={styles.compactLayout}>
          <div className={styles.compactTabBar}>
            <TabList
              selectedValue={outerTab}
              onTabSelect={(_, data) => setOuterTab(data.value as typeof outerTab)}
            >
              <Tab value="input">Input</Tab>
              <Tab value="configure">Configure</Tab>
              <Tab value="output">Output</Tab>
            </TabList>
          </div>
          <div className={styles.compactTabContent}>
            {/* Input tab */}
            {outerTab === 'input' && (
              tokensLoading || upstreamLoading ? (
                <div className={mergeClasses(styles.compactPanelContent, styles.panelContentCentered)}>
                  <Spinner size="small" label="Loading outputs..." />
                </div>
              ) : (
                <WorkflowInputPanel
                  sources={tokenSources}
                  onTokenClick={handleTokenClick}
                  showSearch={true}
                  currentNodeId={node?.id}
                  actionOutputs={combinedPreviewOutputs}
                  hasRunData={hasPreviewData}
                  isLoadingRunData={isLoadingPreviewData}
                  valueSource={inputPanelValueSource}
                  onValueSourceChange={setInputPanelValueSource}
                  onExpressionInsert={handleExpressionInsert}
                />
              )
            )}
            {/* Editor tab */}
            {outerTab === 'configure' && (
              <div className={mergeClasses(styles.centerPanel, styles.centerPanelShrinkable)}>
                {renderTabStrip()}
                <div className={mergeClasses(styles.editorContent, isReadOnly && styles.editorContentReadOnly)}>
                  {activeTab === 'parameters' && showConnectionTab && (
                    <ConnectionErrorBanner
                      nodeId={node.id}
                      operationType={nodeType}
                      onNavigateToConnection={() => setActiveTab('connection')}
                    />
                  )}
                  {renderTabContent()}
                </div>
              </div>
            )}
            {/* Output tab */}
            {outerTab === 'output' && (
              isExecutionMode ? (
                <>
                  <div className={styles.debugModeHeader}>
                    <div className={styles.debugModeLabel}>
                      <BugRegular />
                      <span>Run Data</span>
                    </div>
                    <Badge appearance="filled" color="informative" size="small">
                      Debug Mode
                    </Badge>
                  </div>
                  <RunDataPanel
                    nodeId={node.id}
                    nodeName={node.name}
                  />
                </>
              ) : (
                <div className={styles.compactPanelContent}>
                  <div className={styles.emptyState}>
                    <Text>No output data</Text>
                    <Text size={200}>Run the workflow to see action output</Text>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      ) : (
        <div className={styles.mainContent}>
          {/* Left Panel - Input (data from previous nodes) */}
          <div className={styles.leftPanel} style={{ width: leftPanelWidth }}>
          <div className={styles.panelHeader}>
            <Text className={styles.panelTitle}>Input</Text>
          </div>
          {tokensLoading || upstreamLoading ? (
            <div className={mergeClasses(styles.panelContent, styles.panelContentCentered)}>
              <Spinner size="small" label="Loading outputs..." />
            </div>
          ) : (
            <WorkflowInputPanel
              sources={tokenSources}
              onTokenClick={handleTokenClick}
              showSearch={true}
              currentNodeId={node?.id}
              actionOutputs={combinedPreviewOutputs}
              hasRunData={hasPreviewData}
              isLoadingRunData={isLoadingPreviewData}
              valueSource={inputPanelValueSource}
              onValueSourceChange={setInputPanelValueSource}
              onExpressionInsert={handleExpressionInsert}
            />
          )}
          {/* Resizer handle */}
          <div
            className={mergeClasses(styles.resizer, styles.resizerLeft, resizingPanel === 'left' && styles.resizerActive)}
            onMouseDown={(e) => handleResizeStart('left', e)}
            role="separator"
            aria-label="Resize input panel"
            aria-orientation="vertical"
          />
        </div>

        {/* Center Panel - Editor */}
        <div className={styles.centerPanel}>
          {renderTabStrip()}

          <div className={mergeClasses(styles.editorContent, isReadOnly && styles.editorContentReadOnly)}>
            {/* Connection error banner - only on Parameters tab for operations that need connections */}
            {activeTab === 'parameters' && showConnectionTab && (
              <ConnectionErrorBanner
                nodeId={node.id}
                operationType={nodeType}
                onNavigateToConnection={() => setActiveTab('connection')}
              />
            )}
            {renderTabContent()}
          </div>
        </div>

        {/* Right Panel - Output / Debug Data */}
        <div className={styles.rightPanel} style={{ width: rightPanelWidth }}>
          {/* Resizer handle */}
          <div
            className={mergeClasses(styles.resizer, styles.resizerRight, resizingPanel === 'right' && styles.resizerActive)}
            onMouseDown={(e) => handleResizeStart('right', e)}
            role="separator"
            aria-label="Resize output panel"
            aria-orientation="vertical"
          />
          {isExecutionMode ? (
            // Debug mode: show RunDataPanel with expression evaluations
            <>
              <div className={styles.debugModeHeader}>
                <div className={styles.debugModeLabel}>
                  <BugRegular />
                  <span>Run Data</span>
                </div>
                <Badge appearance="filled" color="informative" size="small">
                  Debug Mode
                </Badge>
              </div>
              <RunDataPanel
                nodeId={node.id}
                nodeName={node.name}
              />
            </>
          ) : (
            // Normal mode: show output panel (populated during workflow runs)
            <>
              <div className={styles.panelHeader}>
                <Text className={styles.panelTitle}>Output</Text>
              </div>
              <div className={styles.panelContent}>
                <div className={styles.emptyState}>
                  <Text>No output data</Text>
                  <Text size={200}>Run the workflow to see action output</Text>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

ExpandedView.displayName = 'ExpandedView';
